import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  memo,
} from "react";
// import * as PIXI from "pixi.js"; // Not needed anymore
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentRound,
  selectAutoPlaySpeed,
  selectBet,
} from "../../../State/selectors";
// import ReelSymbol from "../ReelSymbol/ReelSymbol"; // Not needed - sprites created programmatically
// import { symbolData } from "../../../SymbolData"; // Not needed here
// import ReelController from "./ReelController"; // Old controller
import ReelControllerV2 from "./ReelControllerV2"; // New controller
import { safeInit } from "../../../utilities";
import { Settings } from "../../../settings";
import DimReelOverlay from "../DimReelOverlay/DimReelOverlay";
import { useReelLandingAnimations } from "../../../Hooks/useLandingAnimations";
import { COIN_SYMBOL_IDS } from "../../../SymbolData";

/**
 * Detects if the current round is a Hold & Win trigger (6+ coins).
 * Used to skip spin complete delay for trigger rounds.
 */
const detectHoldAndWinTrigger = (round) => {
  if (!round?.reels || !Array.isArray(round.reels)) return false;

  let coinCount = 0;

  for (let reelIndex = 0; reelIndex < round.reels.length; reelIndex++) {
    const reel = round.reels[reelIndex];
    if (!Array.isArray(reel)) continue;

    for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
      const symbolId = reel[symbolIndex];
      if (COIN_SYMBOL_IDS.includes(symbolId)) {
        coinCount++;
      }
    }
  }

  return coinCount >= 6;
};

const Reel = memo(
  forwardRef(
    (
      {
        reel,
        index,
        symbolWidth,
        symbolHeight,
        symbolsSpriteSheet,
        symbolsSpriteSheetBlurred,
        onReelStop,
      },
      ref
    ) => {
      const dispatch = useDispatch();
      const reelController = useRef(null);
      const symbolsContainerRef = useRef(null);
      const dimOverlayRef = useRef(null);
      const currentRound = useSelector(selectCurrentRound);
      const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
      const bet = useSelector(selectBet);

      // Landing animations hook
      const { triggerReelLandingAnimations } = useReelLandingAnimations();

      // 🎮 Initialize controller once
      useEffect(() => {
        if (symbolsContainerRef.current && !reelController.current) {
          safeInit(() => {
            reelController.current = new ReelControllerV2(
              dispatch,
              symbolsContainerRef.current,
              symbolsSpriteSheet,
              symbolsSpriteSheetBlurred,
              reel, // Initial symbols from API (initialReels)
              index, // reelIndex
              symbolHeight,
              triggerReelLandingAnimations, // Callback for landing animations
              autoPlaySpeed, // Current autoplay speed
              bet // Current bet value
            );
          });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // Update reels when currentRound changes (API data arrives)
      useEffect(() => {
        if (!reelController.current) return;
        const reelData = currentRound?.reels?.[index];
        reelController.current.updateReels(reelData);

        // Update win state and trigger state for last reel only
        if (index === Settings.REEL_COUNT - 1) {
          // Update win state for sound selection
          reelController.current.setHasWinThisSpin?.(currentRound?.win > 0);

          // Update Hold & Win trigger state to skip spin complete delay
          const isHoldAndWinTrigger = detectHoldAndWinTrigger(currentRound);
          reelController.current.setIsHoldAndWinTrigger?.(isHoldAndWinTrigger);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [currentRound]);

      // 📏 Resize handler
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.resizeSymbol(symbolHeight);
      }, [symbolHeight]);

      // ⚡ Auto play speed update
      useEffect(() => {
        if (
          reelController.current &&
          reelController.current.autoPlaySpeed !== autoPlaySpeed
        ) {
          reelController.current.autoPlaySpeed = autoPlaySpeed;
        }
      }, [autoPlaySpeed]);

      // 💰 Bet update - update coin values when bet changes
      useEffect(() => {
        if (!reelController.current) return;
        reelController.current.updateBet(bet);
      }, [bet]);

      // 🧹 Cleanup
      useEffect(() => {
        return () => {
          if (!reelController.current) return;

          // Clear all animations and timeouts to prevent memory leaks
          reelController.current.clearAllTimeouts();

          // Reset controller state on unmount
          reelController.current.isStopping = false;
          reelController.current.hasStopped = true;
          reelController.current.spinStartTime = null;
        };
      }, []);

      // 🎛️ Expose imperative API for external reel control
      useImperativeHandle(ref, () => ({
        // Basic spin/stop (currently implemented)
        startSpin: () => reelController.current?.spin(),
        stopSpin: () => reelController.current?.stopSpin(),

        // Turbo mode (instant drop, no wind-up)
        startTurboSpin: () => reelController.current?.spinTurbo(),
        stopTurboSpin: () => reelController.current?.stopSpinTurbo(),
        startTensionSpin: () => reelController.current?.spinTension(),
        stopTensionSpin: () => reelController.current?.stopSpinTension(),

        getReelController: () => reelController.current,
        getReelSpinComplete: () => reelController.current?.hasStopped ?? false,

        // Expanding wild support
        replaceVisibleSymbols: (targetSymbolId, hideTextures = true) =>
          reelController.current?.replaceVisibleSymbols(
            targetSymbolId,
            hideTextures
          ),
        replaceSymbolAtPosition: (symbolIndex, targetSymbolId) =>
          reelController.current?.replaceSymbolAtPosition(
            symbolIndex,
            targetSymbolId
          ),

        // Dim control methods for tension mode and other effects
        dimReel: () => dimOverlayRef.current?.dimReel(),
        undimReel: () => dimOverlayRef.current?.undimReel(),
      }));

      return (
        <>
          {/* Container for programmatically created sprites (no React components) */}
          <Container
            ref={symbolsContainerRef}
            name={`reel_${index}`}
            y={0}
            x={0}
          />
          <DimReelOverlay
            ref={dimOverlayRef}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
        </>
      );
    }
  ),
  (prev, next) =>
    prev.symbolWidth === next.symbolWidth &&
    prev.symbolHeight === next.symbolHeight &&
    prev.index === next.index
  // Note: currentRound changes are handled by useEffect, not memo
);

export default Reel;
